// "New chat" header button: reset the conversation right now, with no review
// screen. Pure pieces live here so they can be tested without a room.

/**
 * The button is there once there is something to lose or leave behind: the
 * user has said or tapped something (or gone to voice), or the conversation has
 * ended (the header button then skips the review prompt). A chat that has only
 * had Courtney's greeting has nothing to reset, so the button stays out of the
 * header instead of offering a no-op.
 */
export function shouldShowNewChat({
  userHasActed,
  conversationEnded,
}: {
  userHasActed: boolean;
  conversationEnded: boolean;
}): boolean {
  return userHasActed || conversationEnded;
}

/**
 * Tear the current chat down and open a fresh one.
 *
 * Order matters:
 *  1. `markUserEnded` first. It stops any auto-rejoin loop (that is for agents
 *     that drop on their own; this is the user leaving on purpose) and keeps the
 *     agent's departure from being read as a drop.
 *  2. `endSession` disconnects the room so the old agent is left alone in it.
 *  3. `startFresh` remounts the session (new room, empty thread, greeting,
 *     starter chips). It runs even if the disconnect throws: the old room is
 *     abandoned either way and the user must not be stuck on a dead chat.
 * Nothing here marks the conversation "ended", so the review screen is never
 * shown and no feedback is recorded.
 */
export async function resetChat({
  markUserEnded,
  endSession,
  startFresh,
}: {
  markUserEnded: () => void;
  endSession: () => Promise<void>;
  startFresh: () => void;
}): Promise<void> {
  markUserEnded();
  try {
    await endSession();
  } catch (err) {
    console.error("[lk-widget] new chat: ending the old session failed:", err);
  }
  startFresh();
}
